import { useEffect, useState, useMemo } from 'react';
import SEO, { breadcrumbSchema, collectionPageSchema } from '@/components/SEO';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUpDown, Calendar, Clock, Filter, Plus, Building2 } from 'lucide-react';
import { blogArticles, blogCategories, blogCategoryData, getArticleAuthor } from '@/data/blog-articles';
import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const INITIAL_COUNT = 9;

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'recent' | 'oldest'>('recent');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const sorted = useMemo(() => {
    const filtered = activeCategory ? blogArticles.filter((a) => a.category === activeCategory) : [...blogArticles];
    return filtered.sort((a, b) => sortOrder === 'recent' ? new Date(b.date).getTime() - new Date(a.date).getTime() : new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [activeCategory, sortOrder]);

  const featured = sorted[0];
  const rest = sorted.slice(1);
  const visible = rest.slice(0, visibleCount);
  const hasMore = visibleCount < rest.length;

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    blogArticles.forEach((a) => { counts[a.category] = (counts[a.category] || 0) + 1; });
    return counts;
  }, []);

  return (
    <>
      <SEO
        title="Blog — Cygnature Production | Conseils événementiels"
        description="Conseils, tendances et retours d'expérience en événementiel. Découvrez les articles du blog Cygnature Production."
        path="/blog"
        keywords={['blog événementiel','conseils mariage','tendances événementielles','wedding planning']}
        jsonLd={[
          breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Blog', path: '/blog' }]),
          collectionPageSchema({ name: 'Blog Cygnature Production', description: 'Articles, conseils et tendances événementielles', path: '/blog', itemCount: blogArticles.length }),
        ]}
      />
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-3xl md:text-5xl font-bold">Notre blog</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-xl text-sm md:text-base leading-relaxed">
            Conseils, tendances et retours d'expérience pour réussir vos événements. L'expertise Cygnature au service de vos projets.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 lg:px-8 pt-6 md:pt-8 pb-0 bg-background">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3 pb-4">
          <Select value={activeCategory || '__all__'} onValueChange={(v) => { setActiveCategory(v === '__all__' ? null : v); setVisibleCount(INITIAL_COUNT); }}>
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[280px] h-11 rounded-xl bg-card border-border text-sm font-heading font-medium px-4 gap-3">
              <Filter size={15} className="text-primary shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="bottom" align="start">
              <SelectItem value="__all__">Toutes les catégories ({blogArticles.length})</SelectItem>
              {blogCategoryData.map((cat) => {
                const Icon = cat.icon;
                return (
                  <SelectItem key={cat.label} value={cat.label}>
                    <span className="inline-flex items-center gap-2">
                      <Icon size={14} className="text-primary" />
                      {cat.label} ({categoryCounts[cat.label] || 0})
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'recent' | 'oldest')}>
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[180px] h-11 rounded-xl bg-card border-border text-sm font-heading font-medium px-4 gap-3">
              <ArrowUpDown size={15} className="text-primary shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="bottom" align="start">
              <SelectItem value="recent">Plus récent</SelectItem>
              <SelectItem value="oldest">Plus ancien</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="px-6 lg:px-8 py-4 md:py-6 bg-background">
          <div className="max-w-7xl mx-auto">
            <Link to={`/blog/${featured.slug}`} onClick={() => window.scrollTo(0, 0)} className="group block rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/30 transition-colors">
              <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px]">
                <div className="order-2 lg:order-1 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="inline-flex self-start font-heading font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary text-xs mb-5">{featured.category}</span>
                  <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground group-hover:text-primary transition-colors leading-tight">{featured.title}</h2>
                  <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed line-clamp-3">{featured.metaDescription}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">{getArticleAuthor(featured)}</span>
                    <span className="flex items-center gap-1"><Calendar size={13} /> {new Date(featured.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span className="flex items-center gap-1"><Clock size={13} /> {featured.readTime}</span>
                  </div>
                  <div className="mt-8">
                    <span className="inline-flex items-center gap-2 text-sm font-heading font-bold text-primary group-hover:gap-3 transition-all">Lire l'article <ArrowDown size={14} className="-rotate-90" /></span>
                  </div>
                </div>
                <div className="relative flex order-1 lg:order-2 items-center justify-center bg-muted/20 py-12 lg:py-0 overflow-hidden">
                  <span className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-heading font-bold lg:bg-card/80 lg:backdrop-blur lg:border lg:border-border lg:text-foreground">À la une</span>
                  {featured.image ? (
                    <img src={featured.image} alt={featured.title} className="w-full h-full object-cover absolute inset-0" />
                  ) : (
                    <div className="text-primary/15"><Building2 size={80} /></div>
                  )}
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="px-6 lg:px-8 pt-0 pb-12 md:pb-16 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {visible.map((a, i) => (<BlogCard key={a.slug} article={a} index={i} animate />))}
          </div>
          {visible.length === 0 && <p className="text-center text-muted-foreground text-sm py-12">Aucun article dans cette catégorie.</p>}
          {hasMore && (
            <div className="text-center mt-10">
              <button onClick={() => setVisibleCount((c) => c + 9)} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-border text-sm font-heading font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                <Plus size={16} /> Voir plus d'articles
              </button>
            </div>
          )}
        </div>
      </section>

      <CTASection title="Un projet à discuter ?" subtitle="Contactez-nous pour un premier échange gratuit et sans engagement. Nous vous répondons sous 48 à 72h." buttonLabel="Nous contacter" buttonLink="/contact" />
    </>
  );
};

export default Blog;
